import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CloudArrowUpIcon, PhotoIcon, XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';
import { validateImageFile, compressReceiptImage, fileToBase64 } from '../lib/compression';

interface ReceiptUploaderProps {
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  selectedFile?: File;
  isUploading?: boolean;
  className?: string;
}

const ReceiptUploader: React.FC<ReceiptUploaderProps> = ({
  onFileSelect,
  onFileRemove,
  selectedFile,
  isUploading = false,
  className = '',
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isCompressing, setIsCompressing] = useState(false);
  const [error, setError] = useState<string>('');
  const [uploadAttempts, setUploadAttempts] = useState(0);
  const [lastFile, setLastFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (file: File, isRetry: boolean = false) => {
    setError('');

    // Track last file for retry
    if (!isRetry) {
      setLastFile(file);
      setUploadAttempts(1);
    } else {
      setUploadAttempts(prev => prev + 1);
    }

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      return;
    }

    try {
      setIsCompressing(true);

      // Generate preview
      const preview = await fileToBase64(file);
      setPreviewUrl(preview);

      // Compress image
      const compressedFile = await compressReceiptImage(file);

      onFileSelect(compressedFile);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to process image';
      setError(errorMessage);

      // Auto-retry once if this is the first attempt
      if (uploadAttempts === 1 && !isRetry) {
        console.log('First upload attempt failed, retrying...');
        setTimeout(() => {
          if (lastFile) {
            handleFileSelect(lastFile, true);
          }
        }, 1000);
      }
    } finally {
      setIsCompressing(false);
    }
  };

  const handleManualRetry = () => {
    if (lastFile) {
      handleFileSelect(lastFile, true);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleRemoveFile = () => {
    setPreviewUrl('');
    setError('');
    onFileRemove();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={className}>
      <label className="label">Receipt Photo (preferred in case of audits)</label>

      {!selectedFile ? (
        <div
          className={`receipt-preview ${isDragOver ? 'dragover' : ''} p-6 text-center cursor-pointer`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={openFileDialog}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleInputChange}
            className="hidden"
          />

          <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-4">
            <p className="text-sm text-gray-600">
              <span className="font-medium text-red-600 hover:text-red-500">
                Click to upload *REQUIRED*
              </span>{' '}
              or drag and drop
            </p>
            <p className="text-xs text-gray-500 mt-1">PNG, JPG, WebP up to 10MB</p>
            <p className="text-xs text-red-600 font-semibold mt-1">
              Receipt photo is mandatory for CRA compliance
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* File preview */}
          <div className="relative bg-gray-50 rounded-lg p-4">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <PhotoIcon className="h-10 w-10 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
                <p className="text-sm text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
              </div>
              <div className="flex items-center space-x-2">
                {previewUrl && (
                  <button
                    type="button"
                    className="p-2 text-gray-400 hover:text-gray-600"
                    onClick={() => {
                      // Open preview modal (implement later)
                      window.open(previewUrl, '_blank');
                    }}
                  >
                    <EyeIcon className="h-5 w-5" />
                  </button>
                )}
                <button
                  type="button"
                  className="p-2 text-gray-400 hover:text-danger-600"
                  onClick={handleRemoveFile}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Progress indicator */}
            {(isCompressing || isUploading) && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">
                    {isCompressing ? 'Compressing...' : 'Uploading...'}
                  </span>
                  <span className="text-gray-500">{isCompressing ? '50%' : '75%'}</span>
                </div>
                <div className="mt-1 bg-gray-200 rounded-full h-2">
                  <motion.div
                    className="bg-primary-600 h-2 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: isCompressing ? '50%' : '75%' }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Image preview */}
          <AnimatePresence>
            {previewUrl && !isCompressing && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div
                  className="relative w-full rounded-lg border border-gray-200 bg-gray-100 overflow-hidden"
                  style={{ paddingTop: '56.25%' }}
                >
                  <img
                    src={previewUrl}
                    alt="Receipt preview"
                    className="absolute inset-0 w-full h-full object-contain p-2"
                    style={{ objectFit: 'contain' }}
                  />
                </div>
                <p className="text-xs text-gray-500 text-center mt-2">
                  Click the eye icon above to view full size
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Error message with retry button */}
      {error && (
        <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4 space-y-3">
          <div className="flex items-start space-x-3">
            <svg
              className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-900">Upload Failed</h4>
              <p className="text-sm text-red-800 mt-1">{error}</p>
              {uploadAttempts > 0 && (
                <p className="text-xs text-red-700 mt-1">Attempt {uploadAttempts}/2</p>
              )}
            </div>
          </div>
          {uploadAttempts >= 2 && lastFile && (
            <button
              type="button"
              onClick={handleManualRetry}
              disabled={isCompressing}
              className="w-full rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isCompressing ? 'Retrying...' : '🔄 Retry Upload'}
            </button>
          )}
        </div>
      )}

      {/* Upload tips */}
      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-800">Tips for Best Results</h4>
            <div className="mt-2 text-sm text-blue-700 space-y-2">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 mr-2"></div>
                <span>Ensure receipt text is clearly visible and all details are readable</span>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 mr-2"></div>
                <span>Include GST/HST amount if shown on the receipt</span>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 mr-2"></div>
                <span>Use good lighting to help with future OCR processing</span>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 mr-2"></div>
                <span>High-quality photos support audits and improve tax filing accuracy</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptUploader;
